<template>
  <div class="index-r6">
    <c-toolbar type="CYBERBOARD" />
    <CyberboardR6ConfigSelection
      v-model="showLightingApplySelection"
      purpose="lighting-sync"
      @confirm="applyLightingToDevice"
    />
    <CyberboardR6CdcPanel
      :baseData="profileNew.data"
      :defaultData="profileNew.default"
      :syncStatus="syncStatus || (isSyncing ? 'syncInProgress' : '')"
      @ready="onCdcReady"
      @state="onCdcState"
      @data="onCdcData"
      @info="onCdcInfo"
      @error="onCdcError"
    />
    <a-modal
      :visible="isSyncing"
      :title="$t('cyberboardR6.sync')"
      :footer="null"
      :centered="true"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      wrapClassName="r6-sync-modal"
    >
      <CyberboardR6SyncProgress
        :label="syncProgressLabel"
        :percent="syncProgressPercent"
      />
    </a-modal>
    <a-modal
      :visible="showDeviceSettings"
      :title="$t('cyberboardR6.deviceSettings')"
      :footer="null"
      :centered="true"
      :width="360"
      wrapClassName="r6-device-settings-modal"
      @cancel="showDeviceSettings = false"
    >
      <div class="device-setting-row">
        <div class="device-setting-copy">
          <div class="device-setting-title">
            {{ $t("cyberboardR6.transportMode") }}
          </div>
        </div>
        <button
          class="transport-button"
          type="button"
          :disabled="
            connectionState !== 'connected' ||
            isSyncing ||
            isEnteringTransportMode
          "
          :aria-busy="isEnteringTransportMode"
          @click="enterTransportMode"
        >
          {{ $t("cyberboardR6.enterTransportMode") }}
        </button>
      </div>
    </a-modal>
    <CyberboardR6Navigation :lightPosition="lightPosition" />
    <div class="title">
      <span>{{ deviceName }}</span>
      <span v-if="deviceVersion" class="device-version">
        ({{ deviceVersion }})
      </span>
    </div>
    <div class="control-panel">
      <a-radio-group
        :value="lightPosition"
        size="large"
        @change="onLightIndexSelect"
      >
        <a-radio-button
          v-for="(pageIndex, position) in topLightPageIndices"
          :key="pageIndex"
          :value="position"
        >
          {{ $t("light.name") }} {{ position + 1 }}
        </a-radio-button>
      </a-radio-group>
      <button
        v-if="connectionState === 'connected'"
        class="device-settings-button"
        type="button"
        :disabled="isSyncing || isEnteringTransportMode"
        @click="showDeviceSettings = true"
      >
        <i class="iconfont iconsettings" aria-hidden="true" />
        <span>{{ $t("cyberboardR6.deviceSettings") }}</span>
      </button>
    </div>
    <div
      class="cyberboard"
      :style="{ backgroundImage: `url(${images.cyberboard_r2})` }"
    >
      <div class="light" @click="onLightClick">
        <c-led-strip
          :isPlaying="true"
          :brightness="previewBrightness"
          :speed="previewSpeed"
          :frames="previewFrames"
        />
        <img class="border" :src="images.index_light_border" />
      </div>
      <div class="keyboard" @click="onKeyboardClick">
        <c-keyboard-new
          class="c-keyboard"
          :layer="previewLayer"
          :layerIndex="0"
          :selectedIndex="-1"
          disabled
        />
        <img class="border" :src="images.index_keyboard_border" />
      </div>
    </div>
    <div class="buttons">
      <div class="profile-upload">
        <div class="profile">
          <span class="profile-text px">{{ profileNew.fileName }}</span>
        </div>
        <label class="upload" for="r6-profile-file">
          <i class="iconfont iconupload" />
          {{ $t("upload") }}
          <input
            id="r6-profile-file"
            type="file"
            accept=".json"
            @change="onUpload"
          />
        </label>
      </div>
      <c-profile-download
        class="download"
        profileType="windows"
        profileFormat="r6"
        :profile="profileNew.data"
        :fileName="profileNew.fileName"
        :version="'r2'"
      >
        <i class="iconfont icondownload" />
        {{ $t("download") }} json
      </c-profile-download>
      <button
        class="apply"
        type="button"
        :disabled="connectionState !== 'connected' || isSyncing"
        @click="showLightingApplySelection = true"
      >
        <i class="iconfont iconrefresh" aria-hidden="true" />
        {{ $t("cyberboardR6.sync") }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { speedList } from "@/utils";
import { normalizeR6ProfileData } from "./utils/cyberboardR6Upload";
import { isSupportedR6ProfileProduct } from "./utils/cyberboardR6CdcProtocol";
import cyberboardR6SyncMixin from "./utils/cyberboardR6SyncMixin";
import CyberboardR6CdcPanel from "./CyberboardR6CdcPanel.vue";
import CyberboardR6SyncProgress from "./CyberboardR6SyncProgress.vue";
import CyberboardR6ConfigSelection from "./CyberboardR6ConfigSelection.vue";
import CyberboardR6Navigation from "./CyberboardR6Navigation.vue";
import { buildR6SyncSelectionPlan } from "./r6Profile";
import { prepareR6LightingApplyData } from "./utils/cyberboardR6Bridge";
import {
  getR6Page,
  getR6LightPageIndices,
  mergeR6UploadData,
  hasR6ProfileData,
  prepareR6ImportProfile,
  prepareR6ImportDownloadData,
  toLedStripFrames,
} from "./r6Profile";

function profileStem(fileName) {
  let stem = String(fileName || "AM").replace(/\.json$/i, "");
  let previous;
  do {
    previous = stem;
    stem = stem.replace(/\s*\(\d+\)$/i, "").replace(/(?:-CB-R6|-CB)+$/i, "");
  } while (stem !== previous);
  return stem || "AM";
}

export default {
  name: "IndexR6",
  components: {
    CyberboardR6CdcPanel,
    CyberboardR6SyncProgress,
    CyberboardR6ConfigSelection,
    CyberboardR6Navigation,
  },
  mixins: [cyberboardR6SyncMixin],
  data() {
    const position = Number(this.$route?.query?.light) - 1;
    return {
      lightPosition: [0, 1, 2].includes(position) ? position : 0,
      showLightingApplySelection: false,
      showDeviceSettings: false,
      isEnteringTransportMode: false,
      deviceVersion: "",
      deviceName: "CYBERBOARD",
    };
  },
  computed: {
    ...mapState(["profileNew"]),
    ...mapState("assets", { images: "images" }),
    topLightPageIndices() {
      return getR6LightPageIndices();
    },
    currentLightPage() {
      return getR6Page(
        this.profileNew.data,
        this.topLightPageIndices[this.lightPosition]
      );
    },
    previewFrames() {
      return toLedStripFrames(this.currentLightPage);
    },
    previewBrightness() {
      return Number(this.currentLightPage?.lightness ?? 100) / 10;
    },
    previewSpeed() {
      const speed = Number(this.currentLightPage?.speed_ms) || 50;
      const speedIndex = speedList.findIndex((value) => value === speed);
      return speedIndex > -1 ? speedIndex + 1 : 12;
    },
    previewLayer() {
      return this.profileNew.data.key_layer?.layer_data?.[0]?.layer || [];
    },
  },
  methods: {
    onCdcInfo(info) {
      this.deviceVersion = String(info?.productInfo || "").trim();
      this.deviceName = info?.productModel
        ? `CYBERBOARD ${info.productModel}`
        : "CYBERBOARD";
    },
    onLightIndexSelect(event) {
      this.lightPosition = Number(event.target.value);
    },
    async enterTransportMode() {
      if (
        this.connectionState !== "connected" ||
        this.isSyncing ||
        this.isEnteringTransportMode
      ) {
        return;
      }
      this.isEnteringTransportMode = true;
      try {
        await this.r6Bridge.enterTransportMode();
        this.showDeviceSettings = false;
        this.$message.success(this.$t("cyberboardR6.transportModeSuccess"));
      } catch (error) {
        console.error("R6 transport mode request failed", error);
        this.$message.error(this.$t("cyberboardR6.operationFailed"));
      } finally {
        this.isEnteringTransportMode = false;
      }
    },
    onLightClick() {
      this.$router.push({
        path: this.$paths.lightR6,
        query: { light: String(this.lightPosition + 1) },
      });
    },
    onKeyboardClick() {
      this.$router.push(this.$paths.keyboardR6);
    },
    applyLightingToDevice(selection) {
      if (
        this.connectionState !== "connected" ||
        this.isSyncing ||
        !Array.isArray(selection?.led) ||
        !Array.isArray(selection?.axis)
      ) {
        return;
      }
      const plan = buildR6SyncSelectionPlan(
        selection,
        this.profileNew.data.page_data
      );
      if (
        !Object.values(plan.pageFeatureScope).some(
          (features) =>
            features.includes("pageRgb") || features.includes("axisRgb")
        )
      ) {
        return;
      }
      const downloadData = prepareR6LightingApplyData(
        this.profileNew.data,
        plan.pageFeatureScope
      );
      this.requestFullProfileSync(this.profileNew.data, downloadData);
    },
    onUpload(event) {
      const file = event.target.files[0];
      event.target.value = null;
      if (!file) return;
      const reader = new FileReader();
      reader.onerror = () => {
        this.$message.error(this.$t("index.fileErrorInfo"));
      };
      reader.onload = (loadEvent) => {
        try {
          const text = String(loadEvent.target.result || "").replace(
            /^\uFEFF/,
            ""
          );
          const source = JSON.parse(text);
          if (!hasR6ProfileData(source)) {
            throw new Error("invalid R6 profile");
          }
          const normalizedUpload = normalizeR6ProfileData(source);
          if (!normalizedUpload.product_info?.product_id) {
            normalizedUpload.product_info = {
              ...this.profileNew.data.product_info,
              ...normalizedUpload.product_info,
              product_id: "CB_XX",
            };
          }
          if (
            !isSupportedR6ProfileProduct(
              normalizedUpload.product_info.product_id
            )
          ) {
            throw new Error("invalid R6 product");
          }
          const importProfile = prepareR6ImportProfile(
            this.lastSyncedData || this.profileNew.data,
            normalizedUpload,
            {
              hasPageData:
                Array.isArray(source.page_data) && source.page_data.length > 0,
              hasKeyLayer: Object.prototype.hasOwnProperty.call(
                source,
                "key_layer"
              ),
              keyLayerHasValid: Object.prototype.hasOwnProperty.call(
                source.key_layer || {},
                "valid"
              ),
            }
          );
          const uploaded = importProfile.data;
          const deviceConnected = this.connectionState === "connected";
          if (importProfile.partial && deviceConnected) {
            this.$message.info(this.$t("cyberboardR6.partialImport"));
          }
          const data = mergeR6UploadData(this.profileNew.data, uploaded);
          const downloadData = prepareR6ImportDownloadData(
            this.lastSyncedData || data,
            uploaded
          );
          downloadData.__r6PartialImport = importProfile.partial;
          // Keep the merged profile for the editor, but send the uploaded
          // pages plus current controls for slots omitted by a selection export.
          this.requestFullProfileSync(data, downloadData);
          this.$store.dispatch("profileNew/setData", data);
          this.$store.dispatch(
            "profileNew/setFileName",
            profileStem(file.name)
          );
          if (!deviceConnected) {
            this.$message.info(this.$t("cyberboardR6.profileImportLocalOnly"));
          }
        } catch (error) {
          console.error("R6 profile upload failed", error);
          this.$message.error(this.$t("index.fileErrorInfo"));
        }
      };
      reader.readAsText(file);
    },
  },
};
</script>

<style lang="scss" scoped>
.index-r6 {
  min-height: inherit;
  padding: 10px 0 30px;
  color: #ffffff;
  text-align: center;

  .title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 20px;
    font-size: 20px;
  }

  .device-version {
    color: #b8b8b8;
    font-size: 16px;
    font-weight: normal;
  }

  .control-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 1200px;
    max-width: calc(100% - 32px);
    margin: 0 auto 20px;
    text-align: left;
  }

  .device-settings-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    height: 46px;
    padding: 0 16px;
    border: 1px solid #555;
    border-radius: 4px;
    background: transparent;
    color: #fff;
    font-size: 16px;
    line-height: 24px;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus-visible {
      border-color: $primary-color;
      color: $primary-color;
      outline: none;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .iconfont {
      font-size: 20px;
      line-height: 1;
    }
  }

  .cyberboard {
    width: 1200px;
    height: 638px;
    margin: 0 auto 40px;
    background: no-repeat center center / 100% 100%;
    cursor: pointer;

    .light,
    .keyboard {
      position: relative;
      width: 100%;
    }

    .light {
      height: 22.5%;
    }

    .keyboard {
      height: 77.5%;
    }

    .c-keyboard,
    .border {
      position: absolute;
      left: 0;
      width: 100%;
    }

    .c-keyboard {
      top: 0;
    }

    .border {
      top: 0;
      height: 100%;
      opacity: 0;
      pointer-events: none;
    }

    .light:hover .border,
    .keyboard:hover .border {
      opacity: 1;
    }
  }

  .buttons {
    @include flex-center;
    gap: 24px;

    .profile-upload {
      display: flex;
      flex: 0 0 320px;
      width: 320px;
      gap: 0;
    }

    .profile {
      @include nowrap;
      flex: 0 0 43.75%;
      width: 43.75%;
      height: 42px;
      line-height: 42px;
      border: 1px solid $active-color;
      border-right: 0 !important;
      border-radius: 4px 0 0 4px;
      color: $active-color;
      font-size: 12px;
      letter-spacing: 0.2em;
      text-align: left;
      padding: 0 20px;
    }

    .profile-text.px {
      font-size: max(10px, 0.12rem);
    }

    .upload {
      @include button;
      flex: 0 0 56.25%;
      width: 56.25%;
      border-radius: 0 4px 4px 0;
    }

    .download,
    .apply {
      @include button;
      flex: 0 0 320px;
      width: 320px;
      margin: 0;
    }

    input {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.r6-device-settings-modal {
  .ant-modal {
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    border: 1px solid #454545;
    overflow: hidden;
    border-radius: 4px;
    background: #202020;
    color: #fff;
  }

  .ant-modal-header {
    border-bottom-color: #3a3a3a;
    background: transparent;
    text-align: center;
  }

  .ant-modal-title,
  .ant-modal-close {
    color: #fff;
    font-size: 20px;
  }

  .ant-modal-body {
    padding: 20px 24px 24px;
  }

  .device-setting-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 56px;
  }

  .device-setting-copy {
    min-width: 0;
    text-align: center;
  }

  .device-setting-title {
    color: #fff;
    font-size: 18px;
    line-height: 26px;
  }

  .transport-button {
    flex: 0 0 auto;
    width: 120px;
    min-height: 46px;
    padding: 0 16px;
    border: 1px solid #f13a28;
    border-radius: 4px;
    background: #f13a28;
    color: #fff;
    font-size: 16px;
    line-height: 24px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
  }
}
</style>
